<template>
  <div class="pc-root">
    <transition name="pc-toast">
      <div
        v-if="toast.show"
        class="pc-toast"
        :class="'pc-toast--' + toast.type"
        @click="toast.show = false"
      >
        <v-icon
          v-if="toast.type === 'error'"
          size="16"
          color="white"
          class="mr-2"
        >mdi-alert-circle-outline</v-icon>
        <v-icon
          v-else
          size="16"
          color="white"
          class="mr-2"
        >mdi-information-outline</v-icon>
        {{ toast.message }}
      </div>
    </transition>
    <div v-if="!moduleUuid" class="pc-create">
      <!-- Copy-from-template: pre-fill PDF field values from another module -->
      <div class="pc-copy-card">
        <div class="pc-copy-card__header">
          <v-icon size="18" color="var(--app-accent)" class="mr-2">mdi-content-duplicate</v-icon>
          <div class="pc-copy-card__header-info">
            <div class="pc-copy-card__title">Copy PDF Fields from Existing Module</div>
            <div class="pc-copy-card__subtitle">
              Pre-fill this module's PDF field coordinates, value tokens &amp;
              formatting from an existing event. Only the field <strong>values</strong>
              are copied — PDF template files (attachments) are not.
            </div>
          </div>
        </div>

        <div v-if="!availableModules.length" class="pc-copy-card__empty">
          <v-icon size="14" color="var(--app-text-faint)" class="mr-1">mdi-information-outline</v-icon>
          No existing modules available to copy from.
        </div>

        <template v-else>
          <label class="pc-copy-card__label">Template Source</label>
          <ModuleTemplatePicker
            :value="templateSourceUuid"
            :modules="availableModules"
            :loading="copyingFromSource"
            placeholder="Select a module to copy from…"
            @select="selectSource"
          />

          <!-- Summary of what will be copied -->
          <div
            v-if="templateSourceUuid && !copyingFromSource"
            class="pc-copy-summary"
          >
            <div v-if="copyTotal === 0" class="pc-copy-summary__empty">
              <v-icon size="14" color="var(--app-text-muted)" class="mr-1">mdi-information-outline</v-icon>
              The selected module has no PDF fields configured.
            </div>
            <template v-else>
              <div class="pc-copy-summary__head">
                <v-icon size="14" color="#10b981" class="mr-1">mdi-check-circle-outline</v-icon>
                {{ copyTotal }} field{{ copyTotal === 1 ? '' : 's' }} will be
                copied when you create this module:
              </div>
              <div
                v-for="grp in copyGroups"
                :key="grp.key"
                class="pc-copy-summary__group"
              >
                <span class="pc-copy-summary__group-label">{{ grp.label }}</span>
                <span class="pc-copy-summary__group-count">{{ grp.count }}</span>
                <div class="pc-copy-summary__keys">
                  <span
                    v-for="(fk, i) in grp.keys"
                    :key="i"
                    class="pc-copy-summary__chip"
                  >{{ fk }}</span>
                </div>
              </div>
            </template>
          </div>
        </template>
      </div>

      <div class="pc-notice">
        <v-icon size="16" color="var(--app-text-muted)" class="mr-1">mdi-information-outline</v-icon>
        After creating, edit the module to upload PDF templates and fine-tune
        field coordinates with the live preview.
      </div>
    </div>

    <template v-else>
      <!-- Sub-tabs: confirmation_letter / jd14 / t3 -->
      <div class="pc-subtabs">
        <button
          v-for="t in pdfTypes"
          :key="t.key"
          type="button"
          class="pc-subtabs__item"
          :class="{ 'pc-subtabs__item--active': activePdfType === t.key }"
          @click="switchPdfType(t.key)"
        >
          <v-icon size="14" class="mr-1">{{ t.icon }}</v-icon>
          {{ t.label }}
        </button>
      </div>

      <!-- Template upload (dropzone pattern, matches Attachments tab) -->
      <div class="pc-atch-card">
        <div class="pc-atch-card__header">
          <v-icon size="18" color="var(--app-accent)" class="mr-2">mdi-file-pdf-box</v-icon>
          <div class="pc-atch-card__header-info">
            <div class="pc-atch-card__title">{{ activePdfLabel }} Template</div>
            <div class="pc-atch-card__filename">
              {{ currentTemplateName ? currentTemplateName + '.pdf' : 'Not uploaded' }}
            </div>
          </div>
          <button
            v-if="currentTemplateName"
            type="button"
            class="pc-btn-ghost"
            @click="previewTemplate()"
            title="Open this PDF template in a new tab"
          >
            <v-icon size="14" class="mr-1" color="var(--app-accent)">mdi-eye-outline</v-icon>
            Preview
          </button>
        </div>
        <!-- Attendance Form is uploaded in the Attachments tab; here we only
             configure its coordinate overlay, so the dropzone is suppressed. -->
        <div
          v-if="activePdfType !== 'attendance_form'"
          class="pc-atch-card__dropzone"
          @drop.prevent="onTemplateDrop($event)"
          @dragover.prevent
        >
          <div v-if="pendingFile" class="pc-atch-card__selected">
            <v-icon size="18" color="var(--app-accent)">mdi-file-check-outline</v-icon>
            <span>{{ pendingFile.name }}</span>
            <button
              type="button"
              class="pc-atch-card__remove"
              @click.stop="pendingFile = null"
            >
              <v-icon size="14" color="var(--app-text-faint)">mdi-close</v-icon>
            </button>
          </div>
          <div v-else-if="currentTemplateName" class="pc-atch-card__existing">
            <v-icon size="18" color="#10b981">mdi-file-pdf-box</v-icon>
            <span>{{ currentTemplateName }}.pdf</span>
          </div>
          <div v-else class="pc-atch-card__placeholder">
            <v-icon size="24" color="var(--app-text-faint)">mdi-cloud-upload-outline</v-icon>
            <span>Drop PDF here or click to browse</span>
          </div>
          <input
            type="file"
            accept=".pdf"
            class="pc-atch-card__file-input"
            @change="onTemplateFileChange"
          />
        </div>
        <div
          v-else
          class="pc-atch-card__hint pc-atch-card__hint--ext"
        >
          <v-icon size="14" color="var(--app-accent)" class="mr-1">mdi-information-outline</v-icon>
          <span>
            Upload the Attendance Form PDF in the
            <strong>Attachments</strong> tab. This sub-tab is for configuring
            field coordinates only.
          </span>
        </div>
        <div v-if="pendingFile" class="pc-atch-card__hint">
          <v-icon size="12" color="var(--app-text-muted)" class="mr-1">mdi-information-outline</v-icon>
          <span>Please <strong>Update</strong> to save in order to edit</span>
        </div>
      </div>

      <!-- Preview sample dropdown + Download preview button -->
      <div class="pc-preview-picker">
        <template v-if="showPreviewSampleDropdown">
          <label class="pc-preview-picker__label">Preview Sample</label>
          <select
            class="pc-preview-picker__select"
            v-model="previewSampleUuid"
            @change="loadPreviewData()"
          >
            <option :value="null">Mock sample (fresh defaults)</option>
            <option v-for="a in samples" :key="a.uuid" :value="a.uuid">
              {{ a.oriCompanyName || "(no name)" }}
              — {{ formatDate(a.timestampCreated) }}
            </option>
          </select>
        </template>
        <button
          type="button"
          class="pc-btn-primary"
          :disabled="!currentTemplateName || downloadingPreview"
          @click="downloadPreview()"
          title="Download a PDF with all current fields stamped at their coordinates"
        >
          <v-progress-circular
            v-if="downloadingPreview"
            indeterminate
            size="12"
            width="2"
            color="white"
            class="mr-1"
          />
          <v-icon v-else size="14" class="mr-1" color="white">mdi-download-outline</v-icon>
          Download Preview
        </button>
      </div>

      <!-- Split view: field editor (left) + PDF preview (right) -->
      <div class="pc-split">
        <div class="pc-split__left">
          <div class="pc-fields__header">
            <div class="pc-fields__title">Fields ({{ fields.length }})</div>
            <button
              type="button"
              class="pc-btn-ghost"
              @click="addField()"
            >
              <v-icon size="14" class="mr-1" color="#0284c7">mdi-plus</v-icon>
              Add Field
            </button>
          </div>

          <div v-if="!fields.length" class="pc-fields__empty">
            No fields configured. Click "Add Field" to create one.
          </div>

          <div
            v-for="(f, idx) in fields"
            :key="idx"
            class="pc-field"
            :class="{ 'pc-field--selected': selectedIdx === idx }"
            @click="selectedIdx = idx"
          >
            <div class="pc-field__chip">
              <span class="pc-field__chip-num">#{{ idx + 1 }}</span>
              <span class="pc-field__chip-key" :title="f.fieldKey">
                {{ f.fieldKey || "(unnamed)" }}
              </span>
            </div>
            <div class="pc-field__row">
              <div class="pc-field__group pc-field__group--wide">
                <label>Field Key</label>
                <input
                  type="text"
                  v-model="f.fieldKey"
                  placeholder="e.g. clDate"
                />
              </div>
              <div class="pc-field__group">
                <label>Page</label>
                <input
                  type="number"
                  v-model.number="f.pageIndex"
                  min="0"
                />
              </div>
              <button
                type="button"
                class="pc-field__remove"
                @click.stop="removeField(idx)"
                title="Remove field"
              >
                <v-icon size="14" color="#ef4444">mdi-close</v-icon>
              </button>
            </div>
            <div class="pc-field__row">
              <div class="pc-field__group">
                <label>Label</label>
                <input
                  type="text"
                  v-model="f.label"
                  placeholder="Admin label"
                />
              </div>
            </div>
            <div class="pc-field__row">
              <div class="pc-field__group">
                <label>X</label>
                <input type="number" v-model.number="f.x" step="1" />
              </div>
              <div class="pc-field__group">
                <label>Y</label>
                <input type="number" v-model.number="f.y" step="1" />
              </div>
              <div class="pc-field__group">
                <label>Font Size</label>
                <input type="number" v-model.number="f.fontSize" />
              </div>
              <div class="pc-field__group pc-field__group--check">
                <button
                  type="button"
                  class="pc-bold-toggle"
                  :class="{ 'pc-bold-toggle--active': f.bold }"
                  @click.stop="f.bold = !f.bold"
                  :title="f.bold ? 'Bold ON' : 'Bold OFF'"
                >
                  <span class="pc-bold-toggle__label">B</span>
                </button>
              </div>
            </div>
            <div class="pc-field__row">
              <div class="pc-field__group pc-field__group--wide pc-token-dd">
                <label>Value Token</label>
                <button
                  type="button"
                  class="pc-token-trigger"
                  :class="{ 'pc-token-trigger--open': openTokenDropdownIdx === idx }"
                  @click.stop="toggleTokenDropdown(idx)"
                >
                  <span class="pc-token-trigger__label">
                    {{ tokenLabelOf(f.valueToken) }}
                  </span>
                  <svg
                    class="pc-token-trigger__chev"
                    :class="{ 'pc-token-trigger__chev--open': openTokenDropdownIdx === idx }"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clip-rule="evenodd" />
                  </svg>
                </button>
                <div
                  v-if="openTokenDropdownIdx === idx"
                  class="pc-token-panel"
                  @click.stop
                >
                  <div class="pc-token-search">
                    <svg
                      class="pc-token-search__icon"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd"/>
                    </svg>
                    <input
                      ref="tokenSearchInput"
                      v-model="tokenSearchQuery"
                      type="text"
                      placeholder="Search tokens..."
                      class="pc-token-search__input"
                      @click.stop
                    />
                  </div>
                  <div
                    v-for="t in filteredTokens"
                    :key="t.id"
                    class="pc-token-option"
                    :class="{ 'pc-token-option--selected': f.valueToken === t.id }"
                    @click="selectToken(f, t.id)"
                  >
                    <svg
                      v-if="f.valueToken === t.id"
                      class="pc-token-option__check"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path fill-rule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clip-rule="evenodd"/>
                    </svg>
                    <span class="pc-token-option__label">{{ t.label }}</span>
                  </div>
                  <div v-if="!filteredTokens.length" class="pc-token-empty">
                    No tokens match "{{ tokenSearchQuery }}"
                  </div>
                </div>
              </div>
              <div
                class="pc-field__group pc-field__group--wide"
                v-if="takesFormat(f.valueToken)"
              >
                <label>Format (moment)</label>
                <input
                  type="text"
                  v-model="f.valueFormat"
                  placeholder="e.g. Do MMMM YYYY"
                />
              </div>
            </div>
            <div class="pc-field__preview">
              <v-icon size="12" color="var(--app-text-muted)" class="mr-1">mdi-eye-outline</v-icon>
              Resolves to:
              <strong class="ml-1">
                {{ resolvePreview(f) || "(empty / runtime)" }}
              </strong>
            </div>
          </div>
        </div>

        <div class="pc-split__right">
          <div class="pc-preview-toolbar">
            <button
              type="button"
              class="ev-btn ev-btn--ghost ev-btn--sm"
              :disabled="!pdfDoc || currentPage <= 1"
              @click="currentPage--; renderPdfPage()"
            >
              <v-icon size="14">mdi-chevron-left</v-icon>
            </button>
            <span class="pc-preview-toolbar__page">
              Page {{ currentPage }} / {{ totalPages || "-" }}
            </span>
            <button
              type="button"
              class="ev-btn ev-btn--ghost ev-btn--sm"
              :disabled="!pdfDoc || currentPage >= totalPages"
              @click="currentPage++; renderPdfPage()"
            >
              <v-icon size="14">mdi-chevron-right</v-icon>
            </button>
            <div class="pc-preview-toolbar__spacer"></div>
            <button
              type="button"
              class="ev-btn ev-btn--ghost ev-btn--sm"
              @click="zoom = Math.max(0.5, zoom - 0.1); renderPdfPage()"
            >
              <v-icon size="14">mdi-minus</v-icon>
            </button>
            <span class="pc-preview-toolbar__zoom">
              {{ Math.round(zoom * 100) }}%
            </span>
            <button
              type="button"
              class="ev-btn ev-btn--ghost ev-btn--sm"
              @click="zoom = Math.min(2, zoom + 0.1); renderPdfPage()"
            >
              <v-icon size="14">mdi-plus</v-icon>
            </button>
          </div>
          <div class="pc-preview-wrap" ref="previewWrap">
            <div v-if="!currentTemplateName" class="pc-preview-empty">
              <v-icon size="36" color="var(--app-text-faint)">mdi-file-pdf-box</v-icon>
              <span>Upload a PDF template to preview positions</span>
            </div>
            <div v-else-if="loadingPdf" class="pc-preview-empty">
              <v-progress-circular indeterminate color="var(--app-accent)" size="24" width="3" />
              <span>Loading PDF...</span>
            </div>
            <div v-else-if="pdfLoadError" class="pc-preview-empty pc-preview-empty--error">
              <v-icon size="36" color="#ef4444">mdi-file-alert-outline</v-icon>
              <span class="pc-preview-empty__title">File not found</span>
              <span class="pc-preview-empty__hint">
                The <strong>{{ activePdfLabel }}</strong> template file could
                not be loaded. Upload a new PDF in the dropzone above, then
                click Update to save.
              </span>
            </div>
            <div v-else class="pc-preview-canvas-wrap">
              <canvas ref="pdfCanvas" class="pc-preview-canvas"></canvas>
              <div class="pc-preview-overlay" :style="overlayStyle">
                <div
                  v-for="(f, idx) in visibleFieldsOnPage"
                  :key="'ov-' + idx"
                  class="pc-overlay-marker"
                  :class="{ 'pc-overlay-marker--selected': selectedIdx === f.__origIdx }"
                  :style="markerStyle(f)"
                  @mousedown="startDrag($event, f.__origIdx)"
                  @click="selectedIdx = f.__origIdx"
                  :title="f.label || f.fieldKey"
                >
                  <span class="pc-overlay-marker__text">
                    {{ resolvePreview(f) || f.label || f.fieldKey }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="saveError" class="pc-error">{{ saveError }}</div>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import { resolveTokenValue, TOKEN_TAKES_FORMAT } from "./pdfTokenResolver";
import ModuleTemplatePicker from "./ModuleTemplatePicker.vue";

export default {
  name: "PdfConfigPanel",
  components: { ModuleTemplatePicker },
  props: {
    moduleUuid: { type: String, default: "" },
    moduleData: { type: Object, default: null },
    // Live mirror of `form.hasAttendanceForm` from the parent so the
    // Attendance Form sub-tab appears/disappears as the admin toggles the
    // checkbox in the Attachments tab, without waiting for a save+reload.
    hasAttendanceForm: { type: Boolean, default: false },
    serviceUrl: { type: String, required: true },
    authToken: { type: String, required: true },
    // Existing modules offered as "copy from" sources in create mode.
    availableModules: { type: Array, default: () => [] },
  },
  data() {
    return {
      activePdfType: "confirmation_letter",
      fieldsByPdfType: {
        confirmation_letter: [],
        jd14: [],
        t3: [],
        attendance_form: [],
      },
      templateByPdfType: {
        confirmation_letter: null,
        jd14: null,
        t3: null,
        attendance_form: null,
      },
      tokens: [],
      samples: [],
      previewSampleUuid: null,
      previewData: null,
      selectedIdx: -1,
      saveError: "",
      downloadingPreview: false,
      // Mock preview is always used; the real-application picker is hidden.
      // Flip to true if you want to bring back the Preview Sample dropdown.
      showPreviewSampleDropdown: false,
      openTokenDropdownIdx: -1,
      tokenSearchQuery: "",
      pendingFilesByPdfType: {
        confirmation_letter: null,
        jd14: null,
        t3: null,
        attendance_form: null,
      },
      // pdf.js state
      pdfDoc: null,
      currentPage: 1,
      totalPages: 0,
      zoom: 1,
      loadingPdf: false,
      canvasWidth: 0,
      canvasHeight: 0,
      pageHeightPdfUnits: 0,
      pdfRenderScale: 1,
      pdfLoadError: "",
      toast: { show: false, message: "", type: "info" },
      toastTimer: null,
      // drag state
      dragState: null,
      // create-mode: chosen source module to copy PDF field values from
      templateSourceUuid: null,
      copyingFromSource: false,
    };
  },
  computed: {
    // Sub-tab list. Attendance Form is opt-in per module — only show its
    // tab (and run its upload/save in flushAll) when the parent toggles
    // `hasAttendanceForm` on. Order matches the saved-PDF flow: outbound
    // letter first, then JD14/T3 claim docs, then the on-site attendance.
    pdfTypes() {
      const types = [
        {
          key: "confirmation_letter",
          label: "Confirmation Letter",
          icon: "mdi-file-document-outline",
        },
        { key: "jd14", label: "JD14", icon: "mdi-file-chart-outline" },
        { key: "t3", label: "T3", icon: "mdi-file-table-outline" },
      ];
      if (this.hasAttendanceForm) {
        types.push({
          key: "attendance_form",
          label: "Attendance Form",
          icon: "mdi-file-account-outline",
        });
      }
      return types;
    },
    fields() {
      return this.fieldsByPdfType[this.activePdfType] || [];
    },
    currentTemplateName() {
      return this.templateByPdfType[this.activePdfType];
    },
    pendingFile: {
      get() {
        return this.pendingFilesByPdfType[this.activePdfType];
      },
      set(v) {
        this.pendingFilesByPdfType[this.activePdfType] = v;
      },
    },
    activePdfLabel() {
      const t = this.pdfTypes.find((p) => p.key === this.activePdfType);
      return t ? t.label : this.activePdfType;
    },
    overlayStyle() {
      return {
        width: this.canvasWidth + "px",
        height: this.canvasHeight + "px",
      };
    },
    visibleFieldsOnPage() {
      return this.fields
        .map((f, idx) => ({ ...f, __origIdx: idx }))
        .filter((f) => (f.pageIndex || 0) === this.currentPage - 1);
    },
    filteredTokens() {
      const q = (this.tokenSearchQuery || "").trim().toLowerCase();
      if (!q) return this.tokens;
      return this.tokens.filter(
        (t) =>
          t.label.toLowerCase().includes(q) ||
          t.id.toLowerCase().includes(q)
      );
    },
    // Summary of the fields copied from the chosen template source, grouped
    // by PDF type. Drives the create-mode "X fields will be copied" readout.
    copyGroups() {
      const labels = {
        confirmation_letter: "Confirmation Letter",
        jd14: "JD14",
        t3: "T3",
        attendance_form: "Attendance Form",
      };
      return Object.keys(labels)
        .map((key) => {
          const arr = this.fieldsByPdfType[key] || [];
          return {
            key,
            label: labels[key],
            count: arr.length,
            keys: arr.map((f) => f.fieldKey || "(unnamed)"),
          };
        })
        .filter((g) => g.count > 0);
    },
    copyTotal() {
      return this.copyGroups.reduce((sum, g) => sum + g.count, 0);
    },
  },
  watch: {
    moduleUuid: {
      immediate: true,
      handler(v) {
        if (v) this.bootstrap();
      },
    },
    // If the admin toggles off Has Attendance Form while sitting on that
    // sub-tab, fall back to the default so we don't render a tab body that
    // has no matching button in the strip above.
    hasAttendanceForm(val) {
      if (!val && this.activePdfType === "attendance_form") {
        this.switchPdfType("confirmation_letter");
      }
    },
  },
  mounted() {
    document.addEventListener("click", this.handleDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleDocumentClick);
  },
  methods: {
    async bootstrap() {
      const tasks = [this.loadTokens(), this.loadFields()];
      if (this.showPreviewSampleDropdown) tasks.push(this.loadSamples());
      await Promise.all(tasks);
      await this.loadPreviewData();
      await this.loadPdfForCurrentType();
    },
    async loadTokens() {
      try {
        const res = await fetch(
          `${this.serviceUrl}/v1/en/console/event_module/pdf-tokens`,
          { headers: { Authorization: `Bearer ${this.authToken}` } }
        );
        const json = await res.json();
        if (json.status) this.tokens = json.data;
      } catch (_) {
        this.tokens = [];
      }
    },
    async loadSamples() {
      try {
        const res = await fetch(
          `${this.serviceUrl}/v1/en/console/event_module/${this.moduleUuid}/pdf-preview-samples`,
          { headers: { Authorization: `Bearer ${this.authToken}` } }
        );
        const json = await res.json();
        if (json.status) this.samples = json.data;
      } catch (_) {
        this.samples = [];
      }
    },
    async loadFields() {
      try {
        const res = await fetch(
          `${this.serviceUrl}/v1/en/console/event_module/${this.moduleUuid}/pdf-fields`,
          { headers: { Authorization: `Bearer ${this.authToken}` } }
        );
        const json = await res.json();
        if (!json.status) return;
        const grouped = {
          confirmation_letter: [],
          jd14: [],
          t3: [],
          attendance_form: [],
        };
        for (const r of json.data.fields) {
          if (!grouped[r.pdfType]) grouped[r.pdfType] = [];
          grouped[r.pdfType].push({ ...r });
        }
        this.fieldsByPdfType = grouped;
        const em = json.data.eventModule || {};
        this.templateByPdfType = {
          confirmation_letter: em.confirmationLetterTemplate || null,
          jd14: em.jd14Template || null,
          t3: em.t3Template || null,
          attendance_form: em.attendanceFormName || null,
        };
      } catch (err) {
        console.log("loadFields failed", err);
      }
    },
    showToast(message, type = "info", ms = 3000) {
      this.toast = { show: true, message, type };
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toast = { show: false, message: "", type: "info" };
      }, ms);
    },
    async previewTemplate() {
      if (!this.currentTemplateName) return;
      try {
        const res = await fetch(
          `${this.serviceUrl}/v1/en/console/event_module/${this.moduleUuid}/pdf-template/${this.activePdfType}`,
          { headers: { Authorization: `Bearer ${this.authToken}` } }
        );
        if (!res.ok) {
          if (res.status === 404) {
            this.showToast("File not found", "error");
          } else {
            this.showToast(`Preview failed (${res.status})`, "error");
          }
          return;
        }
        const blob = await res.blob();
        const objUrl = window.URL.createObjectURL(blob);
        window.open(objUrl, "_blank");
        setTimeout(() => window.URL.revokeObjectURL(objUrl), 60000);
      } catch (err) {
        this.showToast(err.message || "Preview failed", "error");
      }
    },
    async downloadPreview() {
      if (!this.currentTemplateName) return;
      this.downloadingPreview = true;
      try {
        // POST with the current in-memory field state so the downloaded
        // PDF matches the live overlay exactly — even for unsaved drags.
        const body = {
          fields: this.fields.map((f) => ({
            fieldKey: f.fieldKey,
            x: Number(f.x),
            y: Number(f.y),
            fontSize: f.fontSize === "" ? null : f.fontSize,
            bold: !!f.bold,
            pageIndex: Number(f.pageIndex || 0),
            valueToken: f.valueToken,
            valueFormat: f.valueFormat || null,
          })),
        };
        const res = await fetch(
          `${this.serviceUrl}/v1/en/console/event_module/${this.moduleUuid}/pdf-preview-download/${this.activePdfType}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${this.authToken}`,
            },
            body: JSON.stringify(body),
          }
        );
        if (!res.ok) {
          const txt = await res.text();
          this.saveError = `Download failed (${res.status}): ${txt.slice(0, 200)}`;
          return;
        }
        const blob = await res.blob();
        const objUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = `${this.currentTemplateName}_preview.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(objUrl), 1000);
      } catch (err) {
        this.saveError = err.message;
      } finally {
        this.downloadingPreview = false;
      }
    },
    async loadPreviewData() {
      try {
        const url = new URL(
          `${this.serviceUrl}/v1/en/console/event_module/${this.moduleUuid}/pdf-preview-data`
        );
        if (this.previewSampleUuid) {
          url.searchParams.set("applicationUuid", this.previewSampleUuid);
        }
        const res = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${this.authToken}` },
        });
        const json = await res.json();
        if (json.status) this.previewData = json.data;
      } catch (_) {
        this.previewData = null;
      }
    },
    async switchPdfType(key) {
      this.activePdfType = key;
      this.selectedIdx = -1;
      this.currentPage = 1;
      await this.loadPdfForCurrentType();
    },
    loadPdfJsFromCdn() {
      if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
      if (this._pdfJsLoadPromise) return this._pdfJsLoadPromise;
      this._pdfJsLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src =
          "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/legacy/build/pdf.min.js";
        script.async = true;
        script.onload = () => {
          if (window.pdfjsLib) {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc =
              "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/legacy/build/pdf.worker.min.js";
            resolve(window.pdfjsLib);
          } else {
            reject(new Error("pdfjsLib not found on window after script load"));
          }
        };
        script.onerror = () =>
          reject(new Error("Failed to load pdf.js from CDN"));
        document.head.appendChild(script);
      });
      return this._pdfJsLoadPromise;
    },
    async loadPdfForCurrentType() {
      this.pdfDoc = null;
      this.totalPages = 0;
      this.pdfLoadError = "";
      if (!this.currentTemplateName) return;
      this.loadingPdf = true;
      let loaded = false;
      try {
        const pdfjsLib = await this.loadPdfJsFromCdn();

        const res = await fetch(
          `${this.serviceUrl}/v1/en/console/event_module/${this.moduleUuid}/pdf-template/${this.activePdfType}`,
          { headers: { Authorization: `Bearer ${this.authToken}` } }
        );
        if (!res.ok) {
          let serverMsg = "";
          try {
            const json = await res.json();
            serverMsg = json && json.message ? json.message : "";
          } catch (_) {
            // Non-JSON error body — server message stays empty.
          }
          this.pdfLoadError =
            res.status === 404
              ? serverMsg || "PDF file not found on server"
              : `PDF fetch failed (HTTP ${res.status})`;
          return;
        }
        const buf = await res.arrayBuffer();
        const task = pdfjsLib.getDocument({ data: buf });
        this.pdfDoc = await task.promise;
        this.totalPages = this.pdfDoc.numPages;
        loaded = true;
      } catch (err) {
        this.pdfLoadError = err.message || "Failed to load PDF";
      } finally {
        this.loadingPdf = false;
      }
      if (loaded) {
        await this.$nextTick();
        await this.renderPdfPage();
      }
    },
    async renderPdfPage() {
      if (!this.pdfDoc) return;
      const page = await this.pdfDoc.getPage(this.currentPage);
      const viewport = page.getViewport({ scale: this.zoom });
      const canvas = this.$refs.pdfCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      this.canvasWidth = viewport.width;
      this.canvasHeight = viewport.height;
      this.pageHeightPdfUnits = viewport.height / this.zoom;
      this.pdfRenderScale = this.zoom;
      await page.render({ canvasContext: ctx, viewport }).promise;
    },
    markerStyle(f) {
      // Align the HTML overlay text baseline with pdf-lib's drawText baseline.
      // pdf-lib's Times Roman has ascent ≈ 0.683 of the font size above baseline
      // and a small descent below. In CSS with line-height: 1, the baseline of
      // a text element sits at roughly top + 0.75 * fontSize for Times New Roman
      // (browser-rendered). We position the box's top so the baseline lands at
      // (pageHeight - y) * scale in browser coords.
      const scale = this.pdfRenderScale || 1;
      const fontSizePt = Number(f.fontSize) || 10;
      const fontSizePx = fontSizePt * scale;
      const baselineY = (this.pageHeightPdfUnits - (f.y || 0)) * scale;
      const BASELINE_TO_TOP_RATIO = 0.75;
      const left = (f.x || 0) * scale;
      const top = baselineY - fontSizePx * BASELINE_TO_TOP_RATIO;
      return {
        left: left + "px",
        top: top + "px",
        fontSize: fontSizePx + "px",
        lineHeight: "1",
        fontFamily: '"Times New Roman", Times, serif',
        fontWeight: f.bold ? 700 : 400,
      };
    },
    takesFormat(tokenId) {
      return TOKEN_TAKES_FORMAT[tokenId] || false;
    },
    onTokenChange(f) {
      if (!this.takesFormat(f.valueToken)) f.valueFormat = null;
    },
    toggleTokenDropdown(idx) {
      const opening = this.openTokenDropdownIdx !== idx;
      this.openTokenDropdownIdx = opening ? idx : -1;
      this.tokenSearchQuery = "";
      if (opening) {
        this.$nextTick(() => {
          const ref = this.$refs.tokenSearchInput;
          // ref may be an array when used inside v-for; pick the active one
          const el = Array.isArray(ref) ? ref[0] : ref;
          if (el && el.focus) el.focus();
        });
      }
    },
    selectToken(f, tokenId) {
      f.valueToken = tokenId;
      this.onTokenChange(f);
      this.openTokenDropdownIdx = -1;
      this.tokenSearchQuery = "";
    },
    tokenLabelOf(id) {
      if (!id) return "Select a token...";
      const t = this.tokens.find((x) => x.id === id);
      return t ? t.label : id;
    },
    handleDocumentClick() {
      // Any click anywhere outside (dropdown clicks call .stop) closes the
      // open token dropdown so admins can dismiss without clicking the trigger.
      if (this.openTokenDropdownIdx !== -1) {
        this.openTokenDropdownIdx = -1;
      }
    },
    resolvePreview(f) {
      if (!f || !f.valueToken) return "";
      return resolveTokenValue(f.valueToken, f.valueFormat, this.previewData || {});
    },
    formatDate(d) {
      return d ? moment(d).format("YYYY-MM-DD") : "";
    },
    addField() {
      this.fieldsByPdfType[this.activePdfType].push({
        fieldKey: "newField" + (this.fields.length + 1),
        label: "",
        x: 100,
        y: 500,
        fontSize: 10,
        bold: false,
        pageIndex: this.currentPage - 1,
        valueToken: this.tokens[0] ? this.tokens[0].id : "today",
        valueFormat: null,
      });
      this.selectedIdx = this.fields.length - 1;
    },
    removeField(idx) {
      this.fieldsByPdfType[this.activePdfType].splice(idx, 1);
      if (this.selectedIdx === idx) this.selectedIdx = -1;
    },
    onTemplateFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (file) this.pendingFile = file;
      e.target.value = "";
    },
    onTemplateDrop(evt) {
      const file =
        evt.dataTransfer &&
        evt.dataTransfer.files &&
        evt.dataTransfer.files[0];
      if (file && file.type === "application/pdf") this.pendingFile = file;
    },
    async uploadTemplateForPdfType(pdfType, file) {
      if (!file) return;
      const fd = new FormData();
      fd.append("pdfType", pdfType);
      fd.append("file", file);
      const res = await fetch(
        `${this.serviceUrl}/v1/en/console/event_module/${this.moduleUuid}/pdf-template`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${this.authToken}` },
          body: fd,
        }
      );
      const json = await res.json();
      if (!json.status) {
        throw new Error(json.message || `Upload failed (${pdfType})`);
      }
      this.templateByPdfType[pdfType] = json.data.filename;
      this.pendingFilesByPdfType[pdfType] = null;
    },
    async saveFieldsForPdfType(pdfType, moduleUuid = this.moduleUuid) {
      const fields = this.fieldsByPdfType[pdfType] || [];
      const body = {
        pdfType,
        // uuid is sent as-is for existing rows; copied/new rows carry a null
        // uuid so the backend mints a fresh one (avoids unique collisions
        // with the source module's field uuids).
        fields: fields.map((f) => ({
          uuid: f.uuid || null,
          fieldKey: f.fieldKey,
          label: f.label,
          x: Number(f.x),
          y: Number(f.y),
          fontSize: f.fontSize === "" ? null : f.fontSize,
          bold: !!f.bold,
          pageIndex: Number(f.pageIndex || 0),
          valueToken: f.valueToken,
          valueFormat: f.valueFormat || null,
        })),
      };
      const res = await fetch(
        `${this.serviceUrl}/v1/en/console/event_module/${moduleUuid}/pdf-fields`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.authToken}`,
          },
          body: JSON.stringify(body),
        }
      );
      const json = await res.json();
      if (!json.status) {
        throw new Error(json.message || `Save fields failed (${pdfType})`);
      }
    },
    // Called by parent Form.vue from its submit(). Uploads any pending PDF
    // templates + saves all field rows across every sub-tab, then re-syncs.
    async flushAll() {
      if (!this.moduleUuid) return;
      this.saveError = "";
      try {
        for (const t of this.pdfTypes) {
          const pending = this.pendingFilesByPdfType[t.key];
          if (pending) await this.uploadTemplateForPdfType(t.key, pending);
        }
        for (const t of this.pdfTypes) {
          const fields = this.fieldsByPdfType[t.key];
          if (fields && fields.length) await this.saveFieldsForPdfType(t.key);
        }
        await this.loadFields();
        await this.loadPdfForCurrentType();
      } catch (err) {
        this.saveError = err.message;
        throw err;
      }
    },
    // ── Create-mode: copy field VALUES from an existing module ──────────────
    // Reset the in-memory field buckets back to empty (used when the source
    // dropdown is cleared / changed).
    resetCopiedFields() {
      this.fieldsByPdfType = {
        confirmation_letter: [],
        jd14: [],
        t3: [],
        attendance_form: [],
      };
    },
    selectSource(uuid) {
      if (this.templateSourceUuid === uuid) return;
      this.templateSourceUuid = uuid;
      this.onTemplateSourceChange();
    },
    async onTemplateSourceChange() {
      this.resetCopiedFields();
      if (!this.templateSourceUuid) return;
      this.copyingFromSource = true;
      try {
        await this.loadFieldsFromSource(this.templateSourceUuid);
      } catch (err) {
        this.showToast(err.message || "Failed to copy fields", "error");
        this.templateSourceUuid = null;
      } finally {
        this.copyingFromSource = false;
      }
    },
    // Fetch the source module's PDF fields and stage them in memory, stripping
    // each row's uuid/DB identity so they persist as brand-new rows under the
    // module we're about to create. Templates (attachments) are NOT copied.
    async loadFieldsFromSource(sourceUuid) {
      const res = await fetch(
        `${this.serviceUrl}/v1/en/console/event_module/${sourceUuid}/pdf-fields`,
        { headers: { Authorization: `Bearer ${this.authToken}` } }
      );
      const json = await res.json();
      if (!json.status) {
        throw new Error(json.message || "Failed to load source module fields");
      }
      const grouped = {
        confirmation_letter: [],
        jd14: [],
        t3: [],
        attendance_form: [],
      };
      for (const r of (json.data && json.data.fields) || []) {
        if (!grouped[r.pdfType]) grouped[r.pdfType] = [];
        grouped[r.pdfType].push({
          uuid: null,
          fieldKey: r.fieldKey,
          label: r.label,
          x: r.x,
          y: r.y,
          fontSize: r.fontSize,
          bold: r.bold,
          pageIndex: r.pageIndex,
          valueToken: r.valueToken,
          valueFormat: r.valueFormat,
        });
      }
      this.fieldsByPdfType = grouped;
    },
    // Called by parent Form.vue after a NEW module is created. Saves the
    // copied field rows onto the freshly-created module (by uuid). Attachments
    // are intentionally not copied, so only field rows are persisted.
    async persistCopiedFields(newUuid) {
      if (!newUuid) return;
      const keys = ["confirmation_letter", "jd14", "t3", "attendance_form"];
      for (const key of keys) {
        const fields = this.fieldsByPdfType[key];
        if (fields && fields.length) {
          await this.saveFieldsForPdfType(key, newUuid);
        }
      }
    },
    startDrag(evt, fieldIdx) {
      evt.preventDefault();
      this.selectedIdx = fieldIdx;
      const f = this.fields[fieldIdx];
      if (!f) return;
      const startX = evt.clientX;
      const startY = evt.clientY;
      const origX = f.x;
      const origY = f.y;
      const scale = this.pdfRenderScale || 1;
      const onMove = (e) => {
        const dx = (e.clientX - startX) / scale;
        const dy = (e.clientY - startY) / scale;
        f.x = Math.round(origX + dx);
        f.y = Math.round(origY - dy); // browser Y is inverted vs PDF
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .pc-notice {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  color: var(--app-text-muted);
}

.dark-mode .pc-copy-card {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .pc-copy-card__title {
  color: var(--app-text);
}

.dark-mode .pc-copy-card__subtitle {
  color: var(--app-text-muted);
}

.dark-mode .pc-copy-card__empty {
  color: var(--app-text-faint);
}

.dark-mode .pc-copy-card__label {
  color: var(--app-text);
}

.dark-mode .pc-copy-summary {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

.dark-mode .pc-copy-summary__empty {
  color: var(--app-text-muted);
}

.dark-mode .pc-copy-summary__head {
  color: var(--app-text);
}

.dark-mode .pc-copy-summary__group {
  border-top: 1px dashed var(--app-border);
}

.dark-mode .pc-copy-summary__group-label {
  color: var(--app-text);
}

.dark-mode .pc-copy-summary__chip {
  color: var(--app-text);
  background: var(--app-elevated);
  border: 1px solid var(--app-border);
}

.dark-mode .pc-subtabs {
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .pc-subtabs__item {
  color: var(--app-text-muted);
}

.dark-mode .pc-atch-card {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .pc-atch-card__title {
  color: var(--app-text);
}

.dark-mode .pc-atch-card__filename {
  color: var(--app-text-muted);
}

.dark-mode .pc-atch-card__dropzone {
  border: 2px dashed var(--app-border-strong);
  background: var(--app-card);
}

.dark-mode .pc-atch-card__dropzone:hover {
  background: var(--app-accent-soft);
}

.dark-mode .pc-atch-card__placeholder {
  color: var(--app-text-faint);
}

.dark-mode .pc-atch-card__hint {
  color: var(--app-text-muted);
}

.dark-mode .pc-atch-card__hint--ext {
  background: var(--app-accent-soft);
}

.dark-mode .pc-preview-picker__label {
  color: var(--app-text);
}

.dark-mode .pc-preview-picker__select {
  border: 1px solid var(--app-border);
}

.dark-mode .pc-split__left {
  border: 1px solid var(--app-border);
}

.dark-mode .pc-split__right {
  border: 1px solid var(--app-border);
}

.dark-mode .pc-fields__header {
  /* The header is sticky, so it needs an opaque fill of its own — inheriting
     the light rule's #fff left a white bar under the (light) title text. */
  background: var(--app-card);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .pc-fields__title {
  color: var(--app-text);
}

.dark-mode .pc-fields__empty {
  color: var(--app-text-faint);
}

.dark-mode .pc-field {
  border: 1px solid var(--app-border);
  background: var(--app-card);
}

.dark-mode .pc-field:not(:last-child)::after {
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--app-elevated) 20%,
    var(--app-elevated) 80%,
    transparent 100%
  );
}

.dark-mode .pc-field__chip {
  background: var(--app-elevated);
}

.dark-mode .pc-field__chip-key {
  color: var(--app-text);
}

.dark-mode .pc-field--selected .pc-field__chip {
  background: var(--app-accent-soft);
}

.dark-mode .pc-field__group label {
  color: var(--app-text-muted);
}

.dark-mode .pc-field__group input,
.dark-mode .pc-field__group select {
  border: 1px solid var(--app-border);
}

.dark-mode .pc-field__group--check label {
  color: var(--app-text);
}

.dark-mode .pc-field__preview {
  color: var(--app-text-muted);
  border-top: 1px dashed var(--app-border);
}

.dark-mode .pc-preview-toolbar__page,
.dark-mode .pc-preview-toolbar__zoom {
  color: var(--app-text);
}

.dark-mode .pc-preview-wrap {
  background: var(--app-elevated);
}

.dark-mode .pc-preview-empty {
  color: var(--app-text-faint);
}

.dark-mode .pc-preview-empty--error {
  color: var(--app-neutral-fg);
}

.dark-mode .pc-preview-empty__hint {
  color: var(--app-text-muted);
}

.dark-mode .pc-preview-empty__hint code {
  background: var(--app-elevated);
  color: var(--app-text);
}

.dark-mode .pc-preview-empty__detail {
  color: var(--app-text-faint);
}

.dark-mode .pc-overlay-marker {
  color: var(--app-text);
}

.dark-mode .pc-btn-ghost {
  background: var(--app-card);
}

.dark-mode .pc-btn-ghost:hover {
  background: var(--app-accent-soft);
}

.dark-mode .pc-bold-toggle {
  border: 1px solid var(--app-border);
  background: var(--app-card);
}

.dark-mode .pc-bold-toggle__label {
  color: var(--app-text-faint);
}

.dark-mode .pc-token-trigger {
  background: var(--app-card);
  border: 1px solid var(--app-border);
  color: var(--app-text);
}

.dark-mode .pc-token-trigger:hover {
  border-color: var(--app-border-strong);
}

.dark-mode .pc-token-trigger__chev {
  color: var(--app-text-faint);
}

.dark-mode .pc-token-panel {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

.dark-mode .pc-token-search {
  background: var(--app-card);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .pc-token-search__icon {
  color: var(--app-text-faint);
}

.dark-mode .pc-token-search__input {
  color: var(--app-text);
}

.dark-mode .pc-token-search__input::placeholder {
  color: var(--app-text-faint);
}

.dark-mode .pc-token-empty {
  color: var(--app-text-faint);
}

.dark-mode .pc-token-option {
  color: var(--app-text);
}

.dark-mode .pc-token-option:hover {
  background: var(--app-elevated);
}

.dark-mode .pc-token-option--selected {
  background: var(--app-accent-soft);
}

/* >>> end auto-dark <<< */

.pc-root { font-family: 'Lato', 'Segoe UI', sans-serif; }
.pc-notice {
  display: flex; align-items: center; padding: 12px 16px;
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px;
  color: #64748b; font-size: 0.85rem;
}

/* Create-mode: copy-from-template card.
   Reserve vertical room so the (absolutely-positioned) Template Source
   dropdown panel isn't clipped by the dialog body's overflow when the
   create-mode content is otherwise short. */
.pc-create {
  display: flex; flex-direction: column; gap: 12px;
  min-height: 420px;
}
.pc-copy-card {
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 16px;
}
.pc-copy-card__header { display: flex; align-items: flex-start; margin-bottom: 14px; }
.pc-copy-card__header-info { flex: 1; min-width: 0; }
.pc-copy-card__title { font-size: 0.9rem; font-weight: 700; color: #0f172a; }
.pc-copy-card__subtitle {
  font-size: 0.78rem; color: #64748b; line-height: 1.45; margin-top: 2px;
}
.pc-copy-card__empty {
  display: flex; align-items: center;
  font-size: 0.8rem; color: #94a3b8; font-style: italic;
}
.pc-copy-card__label {
  display: block; font-size: 0.78rem; font-weight: 700;
  color: #334155; margin-bottom: 6px;
}

.pc-copy-summary {
  margin-top: 12px; padding: 12px 14px;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 8px;
}
.pc-copy-summary__empty {
  display: flex; align-items: center;
  font-size: 0.8rem; color: #64748b;
}
.pc-copy-summary__head {
  display: flex; align-items: center;
  font-size: 0.82rem; font-weight: 700; color: #0f172a; margin-bottom: 10px;
}
.pc-copy-summary__group {
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px;
  padding: 6px 0; border-top: 1px dashed #f1f5f9;
}
.pc-copy-summary__group-label {
  font-size: 0.78rem; font-weight: 700; color: #334155; min-width: 130px;
}
.pc-copy-summary__group-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 22px; height: 20px; padding: 0 7px;
  background: #0ea5e9; color: #fff; border-radius: 999px;
  font-size: 0.7rem; font-weight: 700;
}
.pc-copy-summary__keys { display: flex; flex-wrap: wrap; gap: 4px; flex: 1; }
.pc-copy-summary__chip {
  font-size: 0.68rem; color: #0f172a;
  background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 6px;
  padding: 1px 7px;
  font-family: 'Menlo', 'Monaco', monospace;
}
.pc-subtabs {
  display: flex; gap: 4px; margin-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}
.pc-subtabs__item {
  display: flex; align-items: center; padding: 8px 14px;
  background: transparent; border: none; border-bottom: 2px solid transparent;
  color: #64748b; cursor: pointer; font-size: 0.85rem; font-weight: 600;
}
.pc-subtabs__item--active { color: #0ea5e9; border-bottom-color: #0ea5e9; }
.pc-atch-card {
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 14px 16px; margin-bottom: 12px;
}
.pc-atch-card__header {
  display: flex; align-items: center; margin-bottom: 10px;
}
.pc-atch-card__title { font-size: 0.85rem; font-weight: 700; color: #0f172a; }
.pc-atch-card__filename { font-size: 0.75rem; color: #64748b; }
.pc-atch-card__dropzone {
  position: relative;
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  background: #fff;
  min-height: 80px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.pc-atch-card__dropzone:hover {
  border-color: #0ea5e9; background: #f0f9ff;
}
.pc-atch-card__file-input {
  position: absolute; inset: 0; opacity: 0; cursor: pointer;
}
.pc-atch-card__placeholder,
.pc-atch-card__selected,
.pc-atch-card__existing {
  display: flex; align-items: center; gap: 8px;
  font-size: 0.8rem;
}
.pc-atch-card__placeholder { color: #94a3b8; flex-direction: column; gap: 4px; }
.pc-atch-card__selected { color: #0369a1; }
.pc-atch-card__existing { color: #047857; }
.pc-atch-card__remove {
  background: none; border: none; cursor: pointer;
  display: flex; align-items: center; padding: 2px;
  position: relative; z-index: 2;
}
.pc-atch-card__hint {
  display: flex; align-items: center;
  margin-top: 8px;
  font-size: 0.72rem; color: #64748b;
}
/* Bigger info notice used when the dropzone is suppressed (e.g. Attendance
   Form, whose PDF is uploaded in the Attachments tab). */
.pc-atch-card__hint--ext {
  align-items: flex-start;
  padding: 10px 12px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  color: #0c4a6e;
  font-size: 0.78rem;
  line-height: 1.45;
}
.pc-preview-picker {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  margin-bottom: 12px; font-size: 0.8rem;
}
.pc-preview-picker__label { font-weight: 600; color: #0f172a; }
.pc-preview-picker__select {
  padding: 6px 10px; border: 1px solid #e2e8f0;
  border-radius: 6px; min-width: 280px;
  flex: 1;
}
.pc-split {
  display: grid; grid-template-columns: 380px 1fr; gap: 12px;
  min-height: 520px;
}
.pc-split__left {
  border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px;
  /* Scale with the viewport so admins get more vertical room on larger
     screens; clamp ensures it stays usable on small laptops too. */
  max-height: calc(100vh - 320px);
  min-height: 520px;
  overflow-y: auto;
}
.pc-split__right {
  border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px;
  display: flex; flex-direction: column;
}
.pc-fields__header {
  display: flex; justify-content: space-between; align-items: center;
  /* Stick to the top of the scrollable left column so admins always have
     "Add Field" + the count visible while scrolling through field cards. */
  position: sticky;
  top: -10px; /* offset matches .pc-split__left padding so it hugs the edge */
  background: #fff;
  z-index: 5;
  padding: 10px 0 8px;
  margin: -10px 0 8px;
  border-bottom: 1px solid #f1f5f9;
}
.pc-fields__title { font-weight: 700; color: #0f172a; font-size: 0.85rem; }
.pc-fields__empty {
  text-align: center; padding: 24px 12px;
  color: #94a3b8; font-size: 0.8rem;
}
.pc-field {
  position: relative;
  border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 10px 10px 10px 12px;
  margin-bottom: 18px; /* extra breathing room between fields */
  cursor: pointer;
  background: #fff;
  box-sizing: border-box;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s, box-shadow 0.15s;
}
/* Strong visual divider between fields so they're clearly separate */
.pc-field + .pc-field {
  margin-top: 4px;
}
.pc-field:not(:last-child)::after {
  content: "";
  position: absolute;
  left: 16px; right: 16px;
  bottom: -10px;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    #cbd5e1 20%,
    #cbd5e1 80%,
    transparent 100%
  );
}
.pc-field--selected {
  border-color: #0ea5e9;
  box-shadow: 0 0 0 2px rgba(14,165,233,0.15);
}
/* Numbered chip + field-key label header on each card */
.pc-field__chip {
  display: inline-flex; align-items: center; gap: 6px;
  margin-bottom: 8px;
  padding: 3px 10px 3px 4px;
  background: #f1f5f9;
  border-radius: 999px;
  max-width: 100%;
}
.pc-field__chip-num {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 22px; height: 22px;
  background: #0ea5e9; color: #fff;
  border-radius: 999px;
  font-size: 0.7rem; font-weight: 700;
  padding: 0 6px;
}
.pc-field__chip-key {
  font-size: 0.72rem; font-weight: 700; color: #0f172a;
  text-transform: none;
  font-family: 'Menlo', 'Monaco', monospace;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 220px;
}
.pc-field--selected .pc-field__chip {
  background: #e0f2fe;
}
.pc-field--selected .pc-field__chip-num {
  background: #0284c7;
}
.pc-field__row {
  display: flex; gap: 6px; margin-bottom: 6px;
  align-items: flex-end;
}
.pc-field__group {
  flex: 1 1 0; min-width: 0;
  display: flex; flex-direction: column; gap: 2px;
}
.pc-field__group--wide { flex: 2 1 0; }
.pc-field__group--check { flex: 0 0 auto; align-self: end; }
.pc-field__group label {
  font-size: 0.7rem; color: #64748b; font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pc-field__group input,
.pc-field__group select {
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  padding: 4px 6px; border: 1px solid #e2e8f0;
  border-radius: 4px; font-size: 0.8rem;
}
.pc-field__group--check label {
  display: flex; align-items: center; gap: 4px;
  font-size: 0.75rem; color: #0f172a;
}
.pc-field__remove {
  background: none; border: none; cursor: pointer;
  align-self: start; padding: 4px;
}
.pc-field__preview {
  font-size: 0.7rem; color: #64748b;
  padding-top: 4px; border-top: 1px dashed #e2e8f0;
  display: flex; align-items: center;
}
.pc-preview-toolbar {
  display: flex; align-items: center; gap: 6px;
  margin-bottom: 8px;
}
.pc-preview-toolbar__spacer { flex: 1; }
.pc-preview-toolbar__page,
.pc-preview-toolbar__zoom {
  font-size: 0.75rem; color: #0f172a; font-weight: 600;
}
.pc-preview-wrap {
  flex: 1; background: #f1f5f9; border-radius: 8px;
  display: flex; align-items: flex-start; justify-content: center;
  padding: 10px; overflow: auto; min-height: 400px;
}
.pc-preview-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; color: #94a3b8; font-size: 0.85rem; padding: 40px;
  height: 100%; text-align: center;
}
.pc-preview-empty--error { color: #475569; max-width: 420px; margin: 0 auto; }
.pc-preview-empty__title {
  color: #ef4444; font-weight: 700; font-size: 0.95rem; margin-top: 4px;
}
.pc-preview-empty__hint {
  color: #64748b; font-size: 0.8rem; line-height: 1.4;
}
.pc-preview-empty__hint code {
  background: #f1f5f9; padding: 1px 6px; border-radius: 4px;
  font-size: 0.75rem; color: #0f172a;
}
.pc-preview-empty__detail {
  color: #94a3b8; font-size: 0.7rem; font-style: italic;
  padding-top: 4px;
}
.pc-preview-canvas-wrap { position: relative; display: inline-block; }
.pc-preview-canvas { display: block; box-shadow: 0 4px 14px rgba(0,0,0,0.08); }
.pc-preview-overlay {
  position: absolute; top: 0; left: 0;
  pointer-events: none;
}
.pc-overlay-marker {
  position: absolute;
  background: rgba(14, 165, 233, 0.08);
  outline: 1px dashed #0ea5e9;
  color: #0f172a;
  padding: 0;
  white-space: nowrap;
  pointer-events: auto;
  cursor: move;
  user-select: none;
  box-sizing: content-box;
}
.pc-overlay-marker__text {
  display: inline-block;
  line-height: inherit;
  font-family: inherit;
  font-weight: inherit;
  font-size: inherit;
}
.pc-overlay-marker--selected {
  background: rgba(245,158,11,0.18);
  border-color: #d97706;
  color: #92400e;
  z-index: 2;
}
.pc-error {
  color: #b91c1c; font-size: 0.8rem; padding-top: 10px;
}

/* HRDC-blue primary action button */
.pc-btn-primary {
  display: inline-flex; align-items: center; justify-content: center;
  padding: 6px 14px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.78rem; font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(2, 132, 199, 0.25);
  transition: transform 0.08s ease, box-shadow 0.12s ease, opacity 0.12s ease;
}
.pc-btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(2, 132, 199, 0.28);
}
.pc-btn-primary:active:not(:disabled) { transform: translateY(0); }
.pc-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }

/* Ghost/outline button matching the blue theme */
.pc-btn-ghost {
  display: inline-flex; align-items: center;
  padding: 5px 12px;
  background: #fff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  color: #0284c7;
  font-size: 0.75rem; font-weight: 600;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;
}
.pc-btn-ghost:hover {
  background: #f0f9ff;
  border-color: #0ea5e9;
}

/* Header row in the template dropzone card */
.pc-atch-card__header-info { flex: 1; }

/* Bold toggle button — replaces the raw checkbox for a cleaner look */
.pc-bold-toggle {
  width: 32px; height: 30px;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  padding: 0;
  align-self: end;
}
.pc-bold-toggle__label {
  font-family: Georgia, "Times New Roman", serif;
  font-weight: 900;
  font-size: 0.85rem;
  color: #94a3b8;
  line-height: 1;
}
.pc-bold-toggle:hover { border-color: #0ea5e9; }
.pc-bold-toggle--active {
  background: #0ea5e9;
  border-color: #0ea5e9;
  box-shadow: 0 1px 2px rgba(2, 132, 199, 0.3);
}
.pc-bold-toggle--active .pc-bold-toggle__label { color: #fff; }

/* Custom dropdown for Value Token (replaces native <select>) */
.pc-token-dd { position: relative; }
.pc-token-trigger {
  display: flex; align-items: center; justify-content: space-between;
  width: 100%;
  padding: 5px 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.8rem;
  color: #0f172a;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s;
  min-height: 30px;
}
.pc-token-trigger:hover { border-color: #cbd5e1; }
.pc-token-trigger--open,
.pc-token-trigger:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}
.pc-token-trigger__label {
  flex: 1; min-width: 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.pc-token-trigger__chev {
  width: 14px; height: 14px;
  color: #94a3b8;
  flex-shrink: 0; margin-left: 6px;
  transition: transform 0.15s;
}
.pc-token-trigger__chev--open { transform: rotate(180deg); }
.pc-token-panel {
  position: absolute;
  top: calc(100% + 4px); left: 0;
  /* Widen so token labels aren't truncated; still constrained by viewport */
  min-width: 320px;
  max-width: 420px;
  z-index: 30;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 28px -8px rgba(15, 23, 42, 0.18),
              0 4px 10px -4px rgba(15, 23, 42, 0.08);
  max-height: 320px;
  overflow-y: auto;
  padding: 4px;
  animation: pc-token-panel-in 0.12s ease-out;
}
/* Sticky search input at the top of the dropdown panel */
.pc-token-search {
  position: sticky; top: -4px;
  z-index: 1;
  display: flex; align-items: center; gap: 6px;
  padding: 6px 8px;
  margin: -4px -4px 4px;
  background: #fff;
  border-bottom: 1px solid #f1f5f9;
}
.pc-token-search__icon {
  width: 14px; height: 14px;
  color: #94a3b8;
  flex-shrink: 0;
}
.pc-token-search__input {
  flex: 1; min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.78rem;
  color: #0f172a;
  padding: 2px 0;
}
.pc-token-search__input::placeholder { color: #94a3b8; }
.pc-token-empty {
  padding: 12px 8px;
  text-align: center;
  font-size: 0.75rem;
  color: #94a3b8;
  font-style: italic;
}
@keyframes pc-token-panel-in {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}
.pc-token-option {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 0.78rem;
  color: #0f172a;
  cursor: pointer;
  transition: background 0.1s;
}
.pc-token-option:hover { background: #f1f5f9; }
.pc-token-option--selected {
  background: #e0f2fe;
  color: #0c4a6e;
  font-weight: 600;
}
.pc-token-option--selected:hover { background: #bae6fd; }
.pc-token-option__check {
  width: 14px; height: 14px;
  color: #0ea5e9;
  flex-shrink: 0;
}
.pc-token-option__label {
  flex: 1; min-width: 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.pc-token-option:not(.pc-token-option--selected) .pc-token-option__label {
  margin-left: 20px; /* offset where check would be, so labels align */
}

/* Toast (self-contained, no external lib) */
.pc-toast {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 9999;
  display: flex; align-items: center;
  padding: 10px 16px;
  border-radius: 10px;
  color: #fff;
  font-size: 0.8rem; font-weight: 600;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
  cursor: pointer;
  max-width: 360px;
}
.pc-toast--info { background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%); }
.pc-toast--error { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
.pc-toast--success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
.pc-toast-enter-active, .pc-toast-leave-active { transition: all 0.22s ease; }
.pc-toast-enter, .pc-toast-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ============================================================
   Responsive — tablet (<=959px) & mobile (<=599px)
   ============================================================ */
@media (max-width: 959px) {
  /* The field editor (left) and PDF preview (right) stack vertically;
     the left column drops its viewport-height clamp so both flow naturally. */
  .pc-split {
    grid-template-columns: 1fr;
    min-height: 0;
  }
  /* min-width: 0 is the key fix: grid/flex items default to min-width:auto,
     so the preview column refuses to shrink below the PDF canvas's intrinsic
     width (~595px) and blows past the viewport. Letting both tracks shrink to
     0 keeps the layout inside the dialog; the canvas then scrolls inside its
     own pane (below) instead of overflowing the whole panel. */
  .pc-split__left,
  .pc-split__right {
    min-width: 0;
  }
  .pc-split__left {
    max-height: none;
    min-height: 0;
  }
  .pc-split__right {
    min-height: 460px;
  }
}

@media (max-width: 599px) {
  /* Sub-tabs scroll horizontally rather than wrapping. */
  .pc-subtabs {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .pc-subtabs::-webkit-scrollbar {
    display: none;
  }
  .pc-subtabs__item {
    flex: 0 0 auto;
    white-space: nowrap;
    padding: 8px 10px;
  }

  /* Preview-sample picker + download button stack and stop forcing 280px. */
  .pc-preview-picker {
    flex-direction: column;
    align-items: stretch;
  }
  .pc-preview-picker__select {
    min-width: 0;
    width: 100%;
  }
  .pc-preview-picker .pc-btn-primary {
    justify-content: center;
  }

  /* Coordinate inputs (X / Y / Font Size / Bold, etc.) wrap two-up
     instead of being squeezed into one cramped line. */
  .pc-field__row {
    flex-wrap: wrap;
  }
  .pc-field__group {
    flex: 1 1 45%;
  }
  .pc-field__group--wide {
    flex: 1 1 100%;
  }

  /* Token dropdown must never exceed the viewport width. */
  .pc-token-panel {
    min-width: 0;
    width: 100%;
    max-width: calc(100vw - 32px);
  }

  /* Keep the toast inside the screen on a narrow device. */
  .pc-toast {
    left: 16px;
    right: 16px;
    max-width: none;
  }

  /* The PDF page renders at its native pixel width (the field-position
     overlay is pinned to those exact pixels, so the canvas must NOT be
     CSS-scaled or markers would drift). Instead the pane scrolls and the
     canvas centres/left-aligns within it. */
  .pc-preview-wrap {
    justify-content: flex-start;
  }

  /* Template card header: title + long filename + Preview button. Let the
     info column shrink and wrap the filename so it can't force a wider row. */
  .pc-atch-card__header {
    flex-wrap: wrap;
  }
  .pc-atch-card__header-info {
    min-width: 0;
  }
  .pc-atch-card__title,
  .pc-atch-card__filename {
    word-break: break-word;
  }
}
</style>
